/**
 * useServiceWorker - Hook para manejar actualizaciones del Service Worker
 *
 * Detecta cuando hay una nueva versión disponible y permite al usuario
 * actualizar la aplicación sin perder datos.
 */

import { useState, useEffect, useCallback } from 'react';
import { useRegisterSW } from 'virtual:pwa-register/react';

interface UseServiceWorkerReturn {
  /** Hay una actualización disponible */
  needRefresh: boolean;
  /** El SW se registró offline */
  offlineReady: boolean;
  /** Actualizar a la nueva versión */
  updateServiceWorker: () => void;
  /** Cerrar el prompt sin actualizar */
  close: () => void;
}

export function useServiceWorker(): UseServiceWorkerReturn {
  const [offlineReady, setOfflineReady] = useState(false);
  const [needRefresh, setNeedRefresh] = useState(false);

  const {
    needRefresh: [needRefreshState, setNeedRefreshState],
    offlineReady: [offlineReadyState, setOfflineReadyState],
    updateServiceWorker,
  } = useRegisterSW({
    onRegisteredSW(swUrl: string, registration: ServiceWorkerRegistration | undefined) {
      console.log('[SW] Registrado:', swUrl);

      // Verificar actualizaciones cada hora
      if (registration) {
        setInterval(() => {
          registration.update();
        }, 60 * 60 * 1000); // 1 hora
      }
    },
    onRegisterError(error: Error) {
      console.error('[SW] Error de registro:', error);
    },
  });

  // Sincronizar estado
  useEffect(() => {
    setOfflineReady(offlineReadyState);
  }, [offlineReadyState]);

  useEffect(() => {
    setNeedRefresh(needRefreshState);
  }, [needRefreshState]);

  const close = useCallback(() => {
    setOfflineReadyState(false);
    setNeedRefreshState(false);
  }, [setOfflineReadyState, setNeedRefreshState]);

  const handleUpdate = useCallback(() => {
    updateServiceWorker(true);
  }, [updateServiceWorker]);

  return {
    needRefresh,
    offlineReady,
    updateServiceWorker: handleUpdate,
    close,
  };
}
